import React, { useMemo, useState } from 'react'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination
} from '@mui/material'
import McPageHelpers from '../McPageHelpers'
import { useTranslation } from 'react-i18next'

const ForecastTable = ({ data }) => {
  const { t } = useTranslation()
  const empty = !data

  if (empty) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2.5,
          bgcolor: 'rgba(255,255,255,0.7)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="body2">No forecast data available</Typography>
      </Paper>
    )
  }

  const item = data
  const {
    predictionTimeTag = '-',
    actualTimeTags = [],
    countryForecasts = []
  } = item

  const [page, setPage] = useState(0)
  const rowsPerPage = 10
  const usePagination = countryForecasts.length > rowsPerPage

  const visibleRows = useMemo(() => {
    if (!usePagination) return countryForecasts
    const start = page * rowsPerPage
    return countryForecasts.slice(start, start + rowsPerPage)
  }, [countryForecasts, page, usePagination])

  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(255,255,255,0.85)',
          backdropFilter: 'saturate(120%) blur(2px)'
        }}
      >
        <Table stickyHeader size="small" sx={{ minWidth: 520 }}>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  fontWeight: 600,
                  bgcolor: '#f4f6fa',
                  color: '#334155',
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }
              }}
            >
              <TableCell>{t('country')}</TableCell>
              <TableCell>{`${t('prediction')} (${predictionTimeTag})`}</TableCell>
              {actualTimeTags.map(tag => (
                <TableCell key={tag}>{tag}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& td': { borderBottom: '1px solid', borderColor: 'divider' },
              '& tr:hover': { bgcolor: 'rgba(2,132,199,0.06)' }
            }}
          >
            {visibleRows.length > 0
              ? (
                  visibleRows.map(cf => (
                  <TableRow key={cf.partnerCountryCode}>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {McPageHelpers.findCountryName(cf.partnerCountryCode)}
                    </TableCell>
                    <TableCell>{McPageHelpers.numToLocaleString(cf.predictionValue, 'decimal', false, 2) ?? '-'}</TableCell>
                    {(cf.actualValues || []).map((v, i) => (
                      <TableCell key={i}>{McPageHelpers.numToLocaleString(v, 'decimal', false, 2) ?? '-'}</TableCell>
                    ))}
                  </TableRow>
                  ))
                )
              : (
                <TableRow>
                  <TableCell colSpan={2 + actualTimeTags.length} align="center" sx={{ py: 4 }}>
                    No partner forecast data
                  </TableCell>
                </TableRow>
                )}
          </TableBody>
        </Table>

        {usePagination && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <TablePagination
              component="div"
              count={countryForecasts.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
              labelRowsPerPage=""
              showFirstButton
              showLastButton
              size="small"
              sx={{
                '.MuiTablePagination-toolbar': { minHeight: 40, px: 1, py: 0 },
                '.MuiTablePagination-displayedRows': { m: 0 }
              }}
            />
          </Box>
        )}
      </TableContainer>
    </Box>
  )
}

export default ForecastTable
